import { useParams, useNavigate } from 'react-router-dom';
import { useTournament } from '../context/TournamentContext';
import { ArrowLeft, Bird, Star, Trophy, Minus, XCircle } from 'lucide-react';

const iconLabel: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
};

export function PlayerDetailPage() {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const { playerStats } = useTournament();

  const stats = playerStats.find(s => s.playerId === playerId);

  if (!stats) {
    return (
      <div>
        <button className="btn btn-ghost mb-md" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Zpět
        </button>
        <div className="empty-state"><p>Hráč nenalezen nebo zatím nemá statistiky</p></div>
      </div>
    );
  }

  const totalHoles = stats.holesWon + stats.holesHalved + stats.holesLost;
  const wonPct = totalHoles > 0 ? (stats.holesWon / totalHoles) * 100 : 0;
  const halvedPct = totalHoles > 0 ? (stats.holesHalved / totalHoles) * 100 : 0;
  const lostPct = totalHoles > 0 ? (stats.holesLost / totalHoles) * 100 : 0;

  return (
    <div>
      <button className="btn btn-ghost mb-md" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Zpět
      </button>

      {/* Player Header */}
      <div className="card mb-lg" style={{ textAlign: 'center' }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: stats.teamColor, margin: '0 auto 0.75rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.25rem', fontWeight: 800, color: 'white',
          fontFamily: 'var(--font-heading)',
        }}>
          {stats.playerName.charAt(0)}
        </div>
        <h2 style={{ fontSize: '1.5rem' }}>{stats.playerName}</h2>
        <div className="badge" style={{ backgroundColor: stats.teamColor + '20', color: stats.teamColor, marginTop: '0.5rem' }}>
          {stats.teamName}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid-3 mb-lg" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
        <div className="stat-card">
          <div className="stat-card-value">{stats.gamesPlayed}</div>
          <div className="stat-card-label">Her</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{stats.totalHolesPlayed}</div>
          <div className="stat-card-label">Jamek</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value" style={{ color: '#f59e0b' }}>{stats.eagles}</div>
          <div className="stat-card-label" style={iconLabel}><Star size={13} color="#f59e0b" /> Eagle</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value" style={{ color: 'var(--success)' }}>{stats.birdies}</div>
          <div className="stat-card-label" style={iconLabel}><Bird size={13} color="var(--success)" /> Birdie</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value" style={{ color: 'var(--success)' }}>{stats.holesWon}</div>
          <div className="stat-card-label" style={iconLabel}><Trophy size={13} color="var(--success)" /> Vyhrané</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{stats.holesHalved}</div>
          <div className="stat-card-label" style={iconLabel}><Minus size={13} /> Půlené</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value" style={{ color: 'var(--error)' }}>{stats.holesLost}</div>
          <div className="stat-card-label" style={iconLabel}><XCircle size={13} color="var(--error)" /> Prohrané</div>
        </div>
      </div>

      {/* Win Rate Bar */}
      <div className="card mb-lg">
        <h4 style={{ marginBottom: '0.75rem', fontSize: '0.9375rem' }}>Win Rate</h4>
        <div style={{ display: 'flex', height: '32px', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: '0.5rem' }}>
          <div style={{ width: `${wonPct}%`, background: 'var(--success)', transition: 'width 0.5s ease', minWidth: wonPct > 0 ? '2px' : 0 }} />
          <div style={{ width: `${halvedPct}%`, background: 'var(--text-muted)', transition: 'width 0.5s ease', minWidth: halvedPct > 0 ? '2px' : 0 }} />
          <div style={{ width: `${lostPct}%`, background: 'var(--error)', transition: 'width 0.5s ease', minWidth: lostPct > 0 ? '2px' : 0 }} />
        </div>
        <div className="flex justify-between" style={{ fontSize: '0.75rem' }}>
          <span style={{ color: 'var(--success)', ...iconLabel }}><Trophy size={11} /> {Math.round(wonPct)}% Won</span>
          <span style={{ color: 'var(--text-muted)', ...iconLabel }}><Minus size={11} /> {Math.round(halvedPct)}% Halved</span>
          <span style={{ color: 'var(--error)', ...iconLabel }}><XCircle size={11} /> {Math.round(lostPct)}% Lost</span>
        </div>
        <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '2rem', color: 'var(--accent)' }}>
            {stats.winRate}%
          </span>
        </div>
      </div>

      {/* Per-game breakdown */}
      <div className="card">
        <h4 style={{ marginBottom: '1rem', fontSize: '0.9375rem' }}>Rozpis po hrách</h4>
        {stats.perGameStats.length > 0 ? (
          <div className="flex flex-col gap-sm">
            {stats.perGameStats.map((gs) => (
              <div key={gs.gameId} style={{
                padding: '0.75rem',
                background: 'var(--bg-surface)',
                borderRadius: 'var(--radius-md)',
                borderLeft: `3px solid ${gs.won === true ? 'var(--success)' : gs.won === false ? 'var(--error)' : 'var(--text-muted)'}`,
              }}>
                <div className="flex items-center justify-between mb-sm">
                  <div>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 700 }}>
                      {gs.gameType === 'foursome' ? 'Foursome' : `Single`}
                    </span>
                    {gs.partnerName && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                        s {gs.partnerName}
                      </span>
                    )}
                  </div>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontWeight: 700,
                    color: gs.won === true ? 'var(--success)' : gs.won === false ? 'var(--error)' : 'var(--text-muted)',
                  }}>
                    {gs.matchResult}
                  </span>
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  vs {gs.opponentName}
                </div>
                <div className="flex gap-md" style={{ fontSize: '0.75rem' }}>
                  {gs.eagles > 0 && <span style={{ color: '#f59e0b', ...iconLabel }}><Star size={11} /> {gs.eagles}</span>}
                  <span style={iconLabel}><Bird size={11} color="var(--success)" /> {gs.birdies}</span>
                  <span style={{ color: 'var(--success)', ...iconLabel }}><Trophy size={11} /> {gs.holesWon}</span>
                  <span style={iconLabel}><Minus size={11} /> {gs.holesHalved}</span>
                  <span style={{ color: 'var(--error)', ...iconLabel }}><XCircle size={11} /> {gs.holesLost}</span>
                  <span className="text-muted">({gs.holesPlayed} jamek)</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state" style={{ padding: '1rem' }}>
            <p>Zatím žádné odehrané hry</p>
          </div>
        )}
      </div>
    </div>
  );
}
